// Single-label export. Legacy: exportPNG/exportPDF/exportZPL (snapshot L6264-6388).
// Fabric v7: toDataURL({format,quality,multiplier}) options object is retained.
// Shape-clipping for round/oval labels (legacy clipImageToShape) is deferred.
// ZPL uses npm zpl-image's rgbaToZ64 (legacy CDN build exposed an older imageToZ64).
import { jsPDF } from 'jspdf'
import * as pako from 'pako'
import { rgbaToZ64 } from 'zpl-image'
import { getCanvas } from './state'

// zpl-image@0.3.0 expects pako as a global (its CDN build read window.pako).
// As an npm module it doesn't import it itself, so wire it up once.
;(globalThis as any).pako = (globalThis as any).pako || pako
import { labelMm, labelPx } from './canvas'
import { EXPORT_DPI, sheetCapacity } from './constants'

/** Smart multiplier — large image backgrounds don't need 3× (legacy heuristic). */
function exportMultiplier(): number {
  const px = labelPx().w * labelPx().h
  return px > 2_000_000 ? 1 : px > 500_000 ? 2 : 3
}

/** PNG of the LABEL at `multiplier` × its scene px, whatever the zoom or the
 *  pasteboard: crop to the label's element rect and undo the zoom. The grid
 *  overlay is never exported; the background is plain white (see canvas.ts). */
export function snapshotPng(multiplier = exportMultiplier()): string {
  const canvas = getCanvas()
  canvas.discardActiveObject()
  const z = canvas.getZoom() || 1
  const v = canvas.viewportTransform
  const { w, h } = labelPx()
  const flag = canvas as unknown as { __exporting?: boolean }
  flag.__exporting = true
  try {
    return canvas.toDataURL({
      format: 'png', quality: 1, multiplier: multiplier / z,
      left: v[4], top: v[5], width: w * z, height: h * z,
      filter: (o) => !(o as { isGrid?: boolean }).isGrid,
    })
  } finally {
    flag.__exporting = false
    canvas.requestRenderAll()
  }
}

function triggerDownload(href: string, filename: string): void {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
}

// Snapshot clipped to the label's die-cut shape (round/oval → transparent corners).
function shapedSnapshot(): Promise<string> {
  return clipImageToShape(snapshotPng(), state.currentSize.shape)
}

export async function exportPNG(filename = 'label.png'): Promise<string> {
  const dataUrl = await shapedSnapshot()
  triggerDownload(dataUrl, filename)
  return dataUrl
}

/** Build a single-label PDF and return it (callers can .save() or read bytes). */
export async function buildLabelPDF(): Promise<jsPDF> {
  const { w: mmW, h: mmH } = labelMm()
  const dataUrl = await shapedSnapshot()
  const pdf = new jsPDF({ orientation: mmW > mmH ? 'landscape' : 'portrait', unit: 'mm', format: [mmW, mmH] })
  pdf.addImage(dataUrl, 'PNG', 0, 0, mmW, mmH, undefined, 'FAST')
  return pdf
}

export async function exportPDF(filename = 'label.pdf'): Promise<ArrayBuffer> {
  const pdf = await buildLabelPDF()
  pdf.save(filename)
  return pdf.output('arraybuffer')
}

/** Generate ZPL (^GFA raster) for thermal printers at the given DPI. */
export function generateZPL(dpi: number = EXPORT_DPI.thermal): Promise<string> {
  const dotsPerMm = dpi / 25.4
  const { w: mmW, h: mmH } = labelMm()
  const widthDots = Math.round(mmW * dotsPerMm)
  const heightDots = Math.round(mmH * dotsPerMm)

  const canvas = getCanvas()
  const multiplier = Math.max(1, Math.ceil(widthDots / labelPx().w))
  const dataUrl = snapshotPng(multiplier)

  // Draw onto an exact dot-sized canvas, then read RGBA for zpl-image.
  const off = document.createElement('canvas')
  off.width = widthDots
  off.height = heightDots
  const ctx = off.getContext('2d')!
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, widthDots, heightDots)

  return new Promise<string>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0, widthDots, heightDots)
      try {
        const rgba = ctx.getImageData(0, 0, widthDots, heightDots).data
        const res = rgbaToZ64(rgba, widthDots, { black: 50, notrim: true })
        resolve(['^XA', `^FO0,0^GFA,${res.length},${res.length},${res.rowlen},${res.z64}`, '^XZ'].join('\n'))
      } catch (e) {
        reject(e)
      }
    }
    img.onerror = () => reject(new Error('ZPL: image load failed'))
    img.src = dataUrl
  })
}

export async function exportZPL(filename = 'label.zpl', dpi?: number): Promise<string> {
  const zpl = await generateZPL(dpi)
  const blob = new Blob([zpl], { type: 'application/octet-stream' })
  const url = URL.createObjectURL(blob)
  triggerDownload(url, filename)
  URL.revokeObjectURL(url)
  return zpl
}

// ---------------------------------------------------------------------------
// Export modal UI (Phase B). Reproduces the live Export dialog 1-to-1
// (snapshot L1533-1640): preview area (single label / batch printer-strip /
// sheet grid with pagination), an info bar, batch + sheet options, a
// .export-format-btn selector (PDF/PNG/ZPL with the `.selected` class) and a
// Download + Print action row. Wires to the existing export functions above
// and the batch export helpers — no widget/engine logic is changed here.
import { el, openModal, closeModal, toast } from './ui/dom'
import { icon } from './ui/icons'
import { state } from './state'
import { trackEvent } from './analytics'
import { batchExportPDF, buildSheetPDF } from './batch/export'
import { clipImagesToShape } from './shape-clip'
import { batchRenderAllLabels, batchRenderLabel, lastBatchWarnings } from './batch/engine'
import { requireBatchAccess, logExport, maybeCloudNudge } from './funnel'

/** After a batch export: surface rows whose barcode value could not be encoded
 *  (rendered as a red ⚠ note on that label instead of a wrong barcode). */
function reportBatchWarnings(): void {
  const w = lastBatchWarnings()
  if (!w.length) return
  const head = w.slice(0, 3).join(' · ')
  toast(`⚠ ${w.length} label${w.length === 1 ? '' : 's'} with invalid barcode values — ${head}${w.length > 3 ? ` (+${w.length - 3} more)` : ''}`, 8000)
  console.warn('[batch] invalid barcode values:\n' + w.join('\n'))
}
import { isDocumentDesign, isRenderConfigured, renderDocumentPDF, collectTemplateIssues } from './render-api'

import { clipImageToShape, shapeBorderRadius } from './shape-clip'
import { pageCount } from './pages'
import { ensureAdGate } from './ads/gate'
import type { SheetLayout } from './types'
import { markBrand } from './brandMark'

type ExportFormat = 'pdf' | 'png' | 'zpl'

/** Any batch rows with at least one non-empty cell ⇒ batch mode. */
function hasBatchData(): boolean {
  return state.batchData.some((row) => Object.values(row).some((v) => v !== ''))
}

function validBatchRows(): typeof state.batchData {
  return state.batchData.filter((row) => Object.values(row).some((v) => v !== ''))
}

/** Sanitise the design name into a safe filename stem (legacy getExportFilename). */
function exportFilename(ext: string): string {
  const stem = (state.currentDesignName || 'label')
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'label'
  return `${stem}.${ext}`
}

/** Business tier may export ZPL (legacy gated thermal export). */
function canExportZpl(): boolean {
  return state.orgTier === 'business'
}

/**
 * Open the Export modal. Toolbar Export button (and Ctrl+E) call this.
 * Builds the dialog via openModal() so the test-visible .modal-backdrop /
 * .modal-title aliases are present.
 */
/**
 * Print the current label directly — the classic "Print → printer dialog" flow
 * (snapshot L6762-6783). Used by the toolbar Print button, Ctrl+P and the
 * export modal's Print. Resolves true when a print window/tab was opened.
 */
export async function printCurrentLabel(opts: { repeat?: number; startAt?: number } = {}): Promise<boolean> {
  try {
    if (await ensureAdGate('print') === 'deny') return false
    const batch = hasBatchData()
    if (isDocumentDesign() && isRenderConfigured()) {
      // Multi-page document: render server-side and open the PDF in a tab — the
      // browser's viewer prints it (a canvas snapshot would miss the data band).
      const blob = await renderDocumentPDF()
      const url = URL.createObjectURL(blob)
      if (!window.open(url, '_blank')) { toast('Pop-up blocked'); return false }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      return true
    }
    const { w: mmW, h: mmH } = labelMm()
    let html = ''
    let count = 1
    if (batch && validBatchRows().length) {
      // Every row prints (classic batchPrint): tiled onto sheets when a sheet
      // layout is selected, else one page per label. Previously only the first
      // row was printed ("Export > Print only prints one page", customer 2026-09-13).
      if (!(await requireBatchAccess())) return false
      const images = await batchRenderAllLabels(Math.max(1, opts.repeat ?? 1))
      const sheet = state.currentSheet
      count = images.length
      html = sheet
        ? printSheetPagesHtml(await clipImagesToShape(images, sheet.shape), sheet, Math.max(0, opts.startAt ?? 0), 'Print')
        : printPagesHtml(images, mmW, mmH, 'Print')
      logExport('batch'); reportBatchWarnings()
    } else {
      html = printPageHtml(snapshotPng(), mmW, mmH, 'Print')
      logExport('print'); maybeCloudNudge()
    }
    const w = window.open('', '_blank')
    if (!w) { toast('Pop-up blocked'); return false }
    w.document.write(html)
    w.document.close()
    if (count > 1) toast(`Printing ${count} labels`)
    trackEvent('label_printed', { size_id: state.currentSize.id, count })
    return true
  } catch (e) {
    console.error('[export] print failed', e)
    toast('Print failed')
    return false
  }
}

/**
 * Exact-size print page. html/body are pinned to the page box with
 * overflow:hidden so sub-pixel rounding can never spill a blank page 2
 * (customer report); prints on image load and closes itself afterwards.
 */
/** Multi-page print document: each label on its own exact-size page. Prints once
 *  every image has loaded (window.onload), then closes itself. */
export function printPagesHtml(images: string[], mmW: number, mmH: number, title: string): string {
  const pages = images.map((src, i) =>
    `<div class="page"${i === images.length - 1 ? ' style="break-after:auto;page-break-after:auto"' : ''}><img src="${src}"></div>`).join('')
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
    `<style>@page{size:${mmW}mm ${mmH}mm;margin:0}html,body{margin:0;padding:0}` +
    `.page{width:${mmW}mm;height:${mmH}mm;overflow:hidden;break-after:page;page-break-after:always}` +
    `.page img{width:${mmW}mm;height:${mmH}mm;display:block}</style></head>` +
    `<body>${pages}<script>window.onload=function(){window.print()};window.onafterprint=function(){window.close()}<\/script></body></html>`
}

/** Sheet-tiled print document (Avery-style N-up), same geometry as buildSheetPDF. */
export function printSheetPagesHtml(images: string[], sheet: SheetLayout, startAt: number, title: string): string {
  const perSheet = sheetCapacity(sheet)
  const totalPages = Math.max(1, Math.ceil((images.length + startAt) / perSheet))
  const W = sheet.pageWidthMM, H = sheet.pageHeightMM
  let pages = ''
  for (let page = 0; page < totalPages; page++) {
    let cells = ''
    for (let row = 0; row < sheet.rows; row++) {
      for (let col = 0; col < sheet.columns; col++) {
        const cell = row * sheet.columns + col
        if (cell >= perSheet) continue
        const idx = page * perSheet + cell - startAt
        if (idx < 0 || idx >= images.length || !images[idx]) continue
        const x = sheet.marginLeftMM + col * (sheet.labelWidthMM + sheet.gapXMM)
        const y = sheet.marginTopMM + row * (sheet.labelHeightMM + sheet.gapYMM)
        cells += `<img src="${images[idx]}" style="position:absolute;left:${x}mm;top:${y}mm;width:${sheet.labelWidthMM}mm;height:${sheet.labelHeightMM}mm">`
      }
    }
    pages += `<div class="page"${page === totalPages - 1 ? ' style="break-after:auto;page-break-after:auto"' : ''}>${cells}</div>`
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
    `<style>@page{size:${W}mm ${H}mm;margin:0}html,body{margin:0;padding:0}` +
    `.page{position:relative;width:${W}mm;height:${H}mm;overflow:hidden;break-after:page;page-break-after:always}</style></head>` +
    `<body>${pages}<script>window.onload=function(){window.print()};window.onafterprint=function(){window.close()}<\/script></body></html>`
}

export function printPageHtml(img: string, mmW: number, mmH: number, title: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
    `<style>@page{size:${mmW}mm ${mmH}mm;margin:0}` +
    `html,body{margin:0;padding:0;width:${mmW}mm;height:${mmH}mm;overflow:hidden}` +
    `img{width:${mmW}mm;height:${mmH}mm;display:block;page-break-after:avoid;break-after:avoid}</style></head>` +
    `<body><img src="${img}" onload="window.print()">` +
    `<script>window.onafterprint=function(){window.close()}<\/script></body></html>`
}

export function openExportModal(): void {
  // Module-local UI state, scoped to this open instance.
  let format: ExportFormat = 'pdf'
  let repeat = 1
  let startAt = 1 // 1-based "start at label" for sheet layouts
  let sheetPage = 0
  let printerPage = 0

  const batch = hasBatchData()
  const sheet: SheetLayout | null = state.currentSheet
  // Document mode: multi-page designs / data tables render SERVER-side (the data
  // band paginates there). Takes precedence over batch — an invoice has imported
  // rows too, but they feed the document's table, not one-label-per-row export.
  const docMode = isDocumentDesign() && isRenderConfigured()
  const PRINTER_PER_PAGE = 4

  // ---- Preview area --------------------------------------------------------
  const previewBox = el('div', 'bg-slate-800 rounded-lg p-4 mb-4')

  // Single-label preview wrapper.
  const singleWrap = el('div', 'flex flex-col items-center')
  const singleImg = el('img', 'bg-white rounded shadow-lg')
  singleImg.style.maxWidth = '100%'
  singleImg.style.maxHeight = '300px'
  singleWrap.append(singleImg)
  const docNote = el('div', docMode ? 'text-[11px] text-slate-400 text-center mt-2' : 'hidden',
    'Current page preview — the document (all pages + paginating tables) is rendered on export.')
  singleWrap.append(docNote)

  // Batch printer-strip preview.
  const printerWrap = el('div', 'hidden')
  const printerStrip = el('div', 'flex gap-2 overflow-x-auto pb-2 justify-center')
  const printerPager = el('div', 'batch-pagination mt-2')
  const printerPrev = el('button', '', '◀')
  const printerInfo = el('span', 'text-xs text-slate-400', 'Label 1 of 1')
  const printerNext = el('button', '', '▶')
  printerPager.append(printerPrev, printerInfo, printerNext)
  printerWrap.append(printerStrip, printerPager)

  // Batch / single-sheet grid preview.
  const sheetWrap = el('div', 'hidden')
  const sheetContainer = el('div', 'bg-white rounded mx-auto relative overflow-hidden')
  const sheetPager = el('div', 'batch-pagination mt-2')
  const sheetPrev = el('button', '', '◀')
  const sheetInfo = el('span', 'text-xs text-slate-400', 'Sheet 1 of 1')
  const sheetNext = el('button', '', '▶')
  sheetPager.append(sheetPrev, sheetInfo, sheetNext)
  sheetWrap.append(sheetContainer, sheetPager)

  previewBox.append(singleWrap, printerWrap, sheetWrap)

  // ---- Info bar ------------------------------------------------------------
  const infoBar = el('div', 'bg-slate-800/50 rounded-lg px-3 py-2 mb-4 flex items-center gap-3 flex-wrap text-xs text-slate-300')
  const dim = labelMm() // display dims honour orientation (portrait A4 = 210×297)
  const dimSpan = el('span', '', `📐 ${dim.w} × ${dim.h} mm`)
  const sep = el('span', 'text-slate-600', '·')
  const printerSpan = el('span', '', markBrand(`🖨️ ${state.currentSize.brand || state.currentSize.name || 'Label'}`))
  infoBar.append(dimSpan, sep, printerSpan)
  if (docMode) {
    const sep2 = el('span', 'text-slate-600', '·')
    const n = validBatchRows().length
    const docInfo = el('span', 'text-blue-400 font-medium',
      `${pageCount()} page${pageCount() === 1 ? '' : 's'} · document PDF${n ? ` · ${n} data row${n === 1 ? '' : 's'}` : ''}`)
    infoBar.append(sep2, docInfo)
  } else if (batch) {
    const sep2 = el('span', 'text-slate-600', '·')
    const n = validBatchRows().length
    const batchCount = el('span', 'text-blue-400 font-medium', `${n} label${n === 1 ? '' : 's'}`)
    infoBar.append(sep2, batchCount)
  }

  // ---- Batch options -------------------------------------------------------
  const batchSection = el('div', batch && !docMode ? 'mb-4' : 'hidden mb-4')
  const batchRow = el('div', 'flex items-center gap-4 flex-wrap')

  // "Start at label" only relevant when tiling into a sheet.
  const startWrap = el('div', sheet ? 'flex items-center gap-2' : 'hidden flex items-center gap-2')
  const startLabel = el('label', 'text-xs text-slate-400 whitespace-nowrap', 'Start at label:')
  const startInput = el('input', 'prop-input') as HTMLInputElement
  startInput.type = 'number'
  startInput.value = '1'
  startInput.min = '1'
  startInput.style.width = '60px'
  startWrap.append(startLabel, startInput)

  // "Each label ×" repeat stepper.
  const repeatWrap = el('div', 'flex items-center gap-2')
  const repeatLabel = el('label', 'text-xs text-slate-400 whitespace-nowrap', 'Each label ×')
  const repeatStepper = el('div', 'num-stepper')
  repeatStepper.style.width = '72px'
  const repeatDec = el('button', 'num-stepper-btn', '−')
  const repeatInput = el('input') as HTMLInputElement
  repeatInput.type = 'number'
  repeatInput.value = '1'
  repeatInput.min = '1'
  repeatInput.max = '100'
  const repeatInc = el('button', 'num-stepper-btn', '+')
  repeatStepper.append(repeatDec, repeatInput, repeatInc)
  repeatWrap.append(repeatLabel, repeatStepper)

  batchRow.append(startWrap, repeatWrap)
  batchSection.append(batchRow)

  // ---- Single-sheet (non-batch) start option ------------------------------
  const sheetSection = el('div', !batch && sheet && !docMode ? 'mb-4' : 'hidden mb-4')
  const sheetStartRow = el('div', 'flex items-center gap-2')
  const sheetStartLabel = el('label', 'text-xs text-slate-400 whitespace-nowrap', 'Start at label:')
  const sheetStartInput = el('input', 'prop-input') as HTMLInputElement
  sheetStartInput.type = 'number'
  sheetStartInput.value = '1'
  sheetStartInput.min = '1'
  sheetStartInput.style.width = '70px'
  sheetStartRow.append(sheetStartLabel, sheetStartInput)
  sheetSection.append(sheetStartRow)

  // ---- Format selector -----------------------------------------------------
  const formatSection = el('div', batch || docMode ? 'hidden mb-4' : 'mb-4')
  const formatRow = el('div', 'flex gap-2')
  const fmtPdf = el('button', 'export-format-btn selected')
  fmtPdf.innerHTML = '<span class="text-sm">📄</span> PDF'
  const fmtPng = el('button', 'export-format-btn')
  fmtPng.innerHTML = '<span class="text-sm">🖼️</span> PNG'
  const fmtZpl = el('button', 'export-format-btn')
  fmtZpl.innerHTML = '<span class="text-sm">🏭</span> ZPL'
  fmtZpl.title = 'For thermal label printers (Zebra, Honeywell, Intermec, etc.)'
  formatRow.append(fmtPdf, fmtPng)
  if (canExportZpl()) formatRow.append(fmtZpl)
  formatSection.append(formatRow)

  const fmtButtons: Record<ExportFormat, HTMLElement> = { pdf: fmtPdf, png: fmtPng, zpl: fmtZpl }
  function selectFormat(f: ExportFormat): void {
    format = f
    for (const k of Object.keys(fmtButtons) as ExportFormat[]) {
      fmtButtons[k].classList.toggle('selected', k === f)
    }
    updateDownloadLabel()
  }
  fmtPdf.addEventListener('click', () => selectFormat('pdf'))
  fmtPng.addEventListener('click', () => selectFormat('png'))
  fmtZpl.addEventListener('click', () => selectFormat('zpl'))

  // ---- Action row ----------------------------------------------------------
  const downloadBtn = el('button', 'tb-btn primary flex-1 justify-center py-3 text-sm')
  function updateDownloadLabel(): void {
    const fmtName = batch || docMode ? 'PDF' : format.toUpperCase()
    downloadBtn.innerHTML = `${icon('export')} Download ${fmtName}`
  }
  updateDownloadLabel()

  const printBtn = el('button', 'tb-btn flex-1 justify-center py-3 text-sm')
  printBtn.style.background = '#334155'
  printBtn.innerHTML = `${icon('print')} Print`

  // ---- Behaviour -----------------------------------------------------------
  async function renderPreview(): Promise<void> {
    // Hide all three; show the one that applies.
    singleWrap.classList.add('hidden')
    printerWrap.classList.add('hidden')
    sheetWrap.classList.add('hidden')

    if (docMode || (!batch && !sheet)) {
      singleWrap.classList.remove('hidden')
      try {
        singleImg.src = await clipImageToShape(snapshotPng(), state.currentSize.shape)
      } catch {
        /* canvas not ready */
      }
      return
    }

    if (batch && !sheet) {
      // Printer strip — paginate rendered labels, PRINTER_PER_PAGE per page.
      printerWrap.classList.remove('hidden')
      printerStrip.textContent = 'Rendering…'
      const images = await batchRenderAllLabels(repeat)
      const total = images.length
      const pages = Math.max(1, Math.ceil(total / PRINTER_PER_PAGE))
      if (printerPage >= pages) printerPage = pages - 1
      if (printerPage < 0) printerPage = 0
      const from = printerPage * PRINTER_PER_PAGE
      const to = Math.min(from + PRINTER_PER_PAGE, total)
      printerStrip.textContent = ''
      for (let i = from; i < to; i++) {
        const im = el('img', 'batch-preview-label bg-white rounded shadow')
        im.src = images[i]
        im.style.maxHeight = '180px'
        im.style.maxWidth = '140px'
        printerStrip.append(im)
      }
      printerInfo.textContent = total
        ? `Labels ${from + 1}–${to} of ${total}`
        : 'No labels'
      printerPrev.toggleAttribute('disabled', printerPage <= 0)
      printerNext.toggleAttribute('disabled', printerPage >= pages - 1)
      return
    }

    // Sheet grid preview (batch tiled, or a single label repeated into the grid).
    sheetWrap.classList.remove('hidden')
    sheetContainer.textContent = 'Rendering…'
    const layout = sheet as SheetLayout
    const perSheet = sheetCapacity(layout) // partial die-cut grids: fewer than columns*rows
    const skip = batch ? 0 : Math.max(0, startAt - 1)
    const images = batch
      ? await batchRenderAllLabels(repeat)
      : [snapshotPng()]
    const total = images.length
    const slotsTotal = total + skip
    const pages = Math.max(1, Math.ceil(slotsTotal / perSheet))
    if (sheetPage >= pages) sheetPage = pages - 1
    if (sheetPage < 0) sheetPage = 0

    // Build a scaled white "page" with absolutely-positioned label tiles.
    const PAGE_W = 280
    const scale = PAGE_W / layout.pageWidthMM
    sheetContainer.textContent = ''
    sheetContainer.style.width = `${PAGE_W}px`
    sheetContainer.style.height = `${layout.pageHeightMM * scale}px`
    for (let r = 0; r < layout.rows; r++) {
      for (let c = 0; c < layout.columns; c++) {
        const cell = r * layout.columns + c
        if (cell >= perSheet) continue // not a real label position on this sheet
        const dataIdx = sheetPage * perSheet + cell - skip
        const x = layout.marginLeftMM + c * (layout.labelWidthMM + layout.gapXMM)
        const y = layout.marginTopMM + r * (layout.labelHeightMM + layout.gapYMM)
        const tile = el('div')
        tile.style.position = 'absolute'
        tile.style.left = `${x * scale}px`
        tile.style.top = `${y * scale}px`
        tile.style.width = `${layout.labelWidthMM * scale}px`
        tile.style.height = `${layout.labelHeightMM * scale}px`
        // Round/oval die-cut sheets: show the real label outline, not a rectangle.
        tile.style.borderRadius = shapeBorderRadius(layout.shape)
        if (dataIdx >= 0 && dataIdx < total && images[dataIdx]) {
          tile.style.backgroundImage = `url(${images[dataIdx]})`
          tile.style.backgroundSize = 'contain'
          tile.style.backgroundRepeat = 'no-repeat'
          tile.style.backgroundPosition = 'center'
          tile.style.overflow = 'hidden'
        } else {
          tile.style.border = '1px dashed #cbd5e1'
        }
        sheetContainer.append(tile)
      }
    }
    sheetInfo.textContent = `Sheet ${sheetPage + 1} of ${pages}`
    sheetPrev.toggleAttribute('disabled', sheetPage <= 0)
    sheetNext.toggleAttribute('disabled', sheetPage >= pages - 1)
  }

  printerPrev.addEventListener('click', () => { printerPage--; void renderPreview() })
  printerNext.addEventListener('click', () => { printerPage++; void renderPreview() })
  sheetPrev.addEventListener('click', () => { sheetPage--; void renderPreview() })
  sheetNext.addEventListener('click', () => { sheetPage++; void renderPreview() })

  // Repeat stepper wiring.
  const clampRepeat = (v: number) => Math.max(1, Math.min(100, Math.round(v) || 1))
  const setRepeat = (v: number) => {
    repeat = clampRepeat(v)
    repeatInput.value = String(repeat)
    printerPage = 0; sheetPage = 0
    void renderPreview()
  }
  repeatDec.addEventListener('click', () => setRepeat(repeat - 1))
  repeatInc.addEventListener('click', () => setRepeat(repeat + 1))
  repeatInput.addEventListener('change', () => setRepeat(parseFloat(repeatInput.value)))

  // Start-at inputs.
  startInput.addEventListener('change', () => {
    startAt = Math.max(1, parseInt(startInput.value, 10) || 1)
    sheetPage = 0
    void renderPreview()
  })
  sheetStartInput.addEventListener('change', () => {
    startAt = Math.max(1, parseInt(sheetStartInput.value, 10) || 1)
    sheetPage = 0
    void renderPreview()
  })

  /** Pre-render template check: a typo'd filter or field would ship as raw code
   *  or a blank in the PDF. Returns false when the user cancels. */
  function confirmTemplateIssues(): boolean {
    const issues = collectTemplateIssues()
    if (!issues.length) return true
    const list = issues.slice(0, 5).map((i) => `• ${i.where}:\n   ${i.error}`).join('\n')
    const more = issues.length > 5 ? `\n…and ${issues.length - 5} more` : ''
    return confirm(
      `⚠ ${issues.length} expression issue${issues.length === 1 ? '' : 's'} found:\n\n${list}${more}\n\n` +
      'These cells may show raw code or blanks in the PDF. Render anyway?')
  }

  // Download.
  downloadBtn.addEventListener('click', () => { void doDownload() })
  async function doDownload(): Promise<void> {
    downloadBtn.toggleAttribute('disabled', true)
    try {
      if (await ensureAdGate('export') === 'deny') return
      if (docMode) {
        if (!confirmTemplateIssues()) return
        downloadBtn.innerHTML = `${icon('export')} Rendering…`
        const blob = await renderDocumentPDF()
        const url = URL.createObjectURL(blob)
        triggerDownload(url, exportFilename('pdf'))
        URL.revokeObjectURL(url)
        toast('Document PDF rendered')
      } else if (batch) {
        if (!(await requireBatchAccess())) return
        const skip = sheet ? Math.max(0, startAt - 1) : 0
        const n = await batchExportPDF(exportFilename('pdf'), repeat, skip)
        logExport('batch')
        toast(`Exported ${n} label${n === 1 ? '' : 's'}`); reportBatchWarnings()
      } else if (sheet) {
        // Single design tiled into the sheet from the chosen start cell.
        const skip = Math.max(0, startAt - 1)
        const img = await clipImageToShape(snapshotPng(), sheet.shape)
        const perSheet = sheetCapacity(sheet)
        const fill = Math.max(1, perSheet - skip)
        const images = Array.from({ length: fill }, () => img)
        buildSheetPDF(images, sheet, skip).save(exportFilename('pdf'))
        toast('Sheet exported')
      } else {
        if (format === 'png') { await exportPNG(exportFilename('png')); toast('PNG exported') }
        else if (format === 'zpl') { await exportZPL(exportFilename('zpl')); toast('ZPL exported') }
        else { await exportPDF(exportFilename('pdf')); toast('PDF exported') }
        logExport('export'); maybeCloudNudge()
      }
      closeModal()
    } catch (e) {
      console.error('[export] download failed', e)
      const msg = (e as Error)?.message ?? ''
      toast(`Export failed${msg ? `: ${msg.slice(0, 120)}` : ''}`)
    } finally {
      downloadBtn.toggleAttribute('disabled', false)
      updateDownloadLabel()
    }
  }

  // Print — same direct path as the toolbar Print button (legacy doPrint).
  printBtn.addEventListener('click', () => { void doPrint() })
  async function doPrint(): Promise<void> {
    if (docMode && !confirmTemplateIssues()) return
    printBtn.toggleAttribute('disabled', true)
    try {
      const ok = await printCurrentLabel({ repeat, startAt: sheet ? Math.max(0, startAt - 1) : 0 })
      if (ok && !docMode) closeModal()
    } finally {
      printBtn.toggleAttribute('disabled', false)
    }
  }

  // ---- Assemble ------------------------------------------------------------
  const actions = el('div', 'flex gap-2')
  actions.append(downloadBtn, printBtn)

  const hint = el('div', 'mt-3 text-xs text-slate-500 text-center',
    '💡 In your print dialog, select "Actual Size" (not "Fit to Page") for correct dimensions.')

  openModal({
    title: 'Export',
    width: '640px',
    testid: 'export',
    body: [previewBox, infoBar, batchSection, sheetSection, formatSection, actions, hint],
  })

  selectFormat('pdf')
  void renderPreview()
}
